import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutosaveOptions<T> {
  /** Function that performs the save. */
  save: (value: T) => Promise<void>;
  /** Milliseconds of inactivity before save fires. */
  debounceMs?: number;
}

export function useAutosave<T>({ save, debounceMs = 800 }: UseAutosaveOptions<T>) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValue = useRef<T | null>(null);
  const inFlight = useRef(false);

  const flush = useCallback(async () => {
    if (pendingValue.current === null) return;
    const value = pendingValue.current;
    pendingValue.current = null;
    inFlight.current = true;
    setStatus('saving');
    try {
      await save(value);
      setStatus('saved');
      setError(null);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      inFlight.current = false;
      if (pendingValue.current !== null) void flush();
    }
  }, [save]);

  const trigger = useCallback(
    (value: T) => {
      pendingValue.current = value;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void flush();
      }, debounceMs);
    },
    [flush, debounceMs],
  );

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return { status, error, trigger };
}
